import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  HairlineCard,
  Icons,
  Link,
  Stack,
  StatusChip,
  TextField,
  Typography,
  iconSize,
  statusTone,
} from '@munifocus/ui';
import {
  APPLICATION_NEXT,
  type PermitAnswers,
  type PermitApplicationView,
  type PermitFieldDefinition,
} from '@munifocus/contracts-gov';
import { AppShell } from '../../components/AppShell';
import { useScope } from '../../providers/ScopeProvider';
import { formatDate, formatDateTime } from '../../lib/relative-time';
import { useMutationToast } from '../../lib/mutation-toast';
import {
  downloadApplicationDocument,
  downloadPermit,
  formatFee,
  useActiveMunicipality,
  useModuleSwitches,
  usePermitApplication,
  usePermitDecision,
  type PermitDecision,
} from '../../lib/permits-api';
import { ScheduleInspectionDialog } from '../inspections/ScheduleInspectionDialog';

type Prompt = 'request-info' | 'payment' | 'approve' | 'refuse' | null;

function answerText(
  field: PermitFieldDefinition,
  value: PermitAnswers[string] | undefined,
  fr: boolean,
  yesNo: (v: boolean) => string,
): string {
  if (value === undefined || value === null || value === '') return '—';
  if (field.type === 'checkbox') return yesNo(Boolean(value));
  if (field.type === 'select') {
    const option = field.options?.find((o) => o.value === value);
    return option ? (fr ? option.labelFr : option.labelEn) : String(value);
  }
  return String(value);
}

/**
 * One application, and every decision on it. The applicant sees the outcome
 * and its reason through their own link; staff names stay here.
 */
export function PermitApplicationDetail() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { can } = useScope();
  const toast = useMutationToast();
  const lg = useActiveMunicipality();
  const modules = useModuleSwitches();
  const query = usePermitApplication(lg, id);
  const decide = usePermitDecision(lg, id);
  const [prompt, setPrompt] = useState<Prompt>(null);
  const [text, setText] = useState('');
  const [waive, setWaive] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const fr = i18n.language.startsWith('fr');
  const app = query.data;
  const canReview = can('permit:review');

  const run = async (decision: PermitDecision, successKey: string) => {
    try {
      await decide.mutateAsync(decision);
      toast.success(successKey);
      setPrompt(null);
      setText('');
      setWaive(false);
    } catch (err) {
      toast.error(err, 'gov.admin.errors.actionFailed');
    }
  };

  const back = { label: t('gov.permits.title'), onClick: () => navigate('/permits') };

  if (query.isLoading || !lg) {
    return (
      <AppShell back={back} title={t('gov.permits.detail.title')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </AppShell>
    );
  }
  if (!app) {
    return (
      <AppShell back={back} title={t('gov.permits.detail.title')}>
        <Alert severity="warning">{t('gov.permits.detail.notFound')}</Alert>
      </AppShell>
    );
  }

  const next = APPLICATION_NEXT[app.status];
  const open = next.length > 0;

  return (
    <AppShell back={back} title={app.reference}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ alignItems: { sm: 'center' } }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h1" sx={{ fontSize: 24 }}>
              {fr ? app.typeNameFr : app.typeNameEn}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {app.reference} · {t('gov.permits.fields.submitted')}{' '}
              {formatDate(app.submittedAt, i18n.language)}
            </Typography>
          </Box>
          <StatusChip
            status={statusTone.permitApplication[app.status]}
            label={t(`gov.permits.statuses.${app.status}`)}
          />
        </Stack>

        {canReview && open && (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {app.status === 'submitted' && (
              <Button
                variant="outlined"
                disabled={decide.isPending}
                onClick={() => void run({ action: 'review' }, 'gov.permits.detail.reviewStarted')}
              >
                {t('gov.permits.actions.review')}
              </Button>
            )}
            {next.includes('info_requested') && (
              <Button variant="outlined" onClick={() => setPrompt('request-info')}>
                {t('gov.permits.actions.requestInfo')}
              </Button>
            )}
            {app.feeStatus === 'due' && (
              <Button variant="outlined" onClick={() => setPrompt('payment')}>
                {t('gov.permits.actions.recordPayment')}
              </Button>
            )}
            {modules.inspections && can('inspection:schedule') && (
              <Button
                variant="outlined"
                startIcon={<Icons.Calendar size={iconSize.sm} />}
                onClick={() => setScheduling(true)}
              >
                {t('gov.permits.actions.scheduleInspection')}
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button color="error" onClick={() => setPrompt('refuse')}>
              {t('gov.permits.actions.refuse')}
            </Button>
            <Button variant="contained" onClick={() => setPrompt('approve')}>
              {t('gov.permits.actions.approve')}
            </Button>
          </Stack>
        )}

        {app.status === 'info_requested' && app.infoRequest && (
          <Alert severity="warning">
            {t('gov.permits.detail.waitingOnApplicant')} {app.infoRequest}
          </Alert>
        )}
        {app.status === 'refused' && app.decisionReason && (
          <Alert severity="error">
            {t('gov.permits.detail.refusedBecause')} {app.decisionReason}
          </Alert>
        )}
        {app.requiresInspection && (
          <Alert severity={app.inspectionOutcome === 'pass' ? 'success' : 'info'}>
            {app.inspectionOutcome
              ? t(`gov.permits.detail.inspection.${app.inspectionOutcome}`)
              : t('gov.permits.detail.inspection.required')}
          </Alert>
        )}

        {app.permit && (
          <HairlineCard sx={{ p: 2.5 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Icons.FileBadge size={iconSize.md} />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  {t('gov.permits.detail.permitIssued', { number: app.permit.number })}
                </Typography>
                <StatusChip
                  status={statusTone.permit[app.permit.status]}
                  label={t(`gov.permits.permitStatuses.${app.permit.status}`)}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('gov.permits.fields.issued')} {formatDate(app.permit.issuedAt, i18n.language)} ·{' '}
                {t('gov.permits.fields.expires')}{' '}
                {app.permit.expiresAt
                  ? formatDate(app.permit.expiresAt, i18n.language)
                  : t('gov.permits.noExpiry')}
              </Typography>
              {app.permit.conditions && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {app.permit.conditions}
                </Typography>
              )}
              <Stack direction="row" spacing={1}>
                {(['en', 'fr'] as const).map((locale) => (
                  <Button
                    key={locale}
                    size="small"
                    startIcon={<Icons.Download size={iconSize.sm} />}
                    onClick={() =>
                      downloadPermit(lg, app.permit!.id, app.permit!.number, locale).catch((err) =>
                        toast.error(err, 'gov.permits.errors.download'),
                      )
                    }
                  >
                    {t(locale === 'en' ? 'gov.permits.downloadEn' : 'gov.permits.downloadFr')}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </HairlineCard>
        )}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2.5}
          sx={{ alignItems: 'flex-start' }}
        >
          <HairlineCard sx={{ p: 2.5, flex: 1, width: '100%' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              {t('gov.permits.detail.applicant')}
            </Typography>
            <Detail label={t('gov.permits.fields.applicant')} value={app.applicantName} />
            {app.organizationName && (
              <Detail label={t('gov.permits.fields.organization')} value={app.organizationName} />
            )}
            <Detail
              label={t('gov.permits.fields.email')}
              value={<Link href={`mailto:${app.applicantEmail}`}>{app.applicantEmail}</Link>}
            />
            {app.applicantPhone && (
              <Detail label={t('gov.permits.fields.phone')} value={app.applicantPhone} />
            )}
            <Detail
              label={t('gov.permits.fields.language')}
              value={t(`gov.permits.languages.${app.applicantLocale}`)}
            />
            <Detail label={t('gov.permits.fields.property')} value={app.propertyAddress} />
            {app.pid && <Detail label={t('gov.permits.fields.pid')} value={app.pid} />}
            <Detail
              label={t('gov.permits.fields.fee')}
              value={
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <span>{formatFee(app.feeCents, i18n.language)}</span>
                  <StatusChip
                    status={statusTone.permitFee[app.feeStatus]}
                    label={t(`gov.permits.feeStatuses.${app.feeStatus}`)}
                  />
                  {app.paymentReference && (
                    <Typography variant="caption" color="text.secondary">
                      {app.paymentReference}
                    </Typography>
                  )}
                </Stack>
              }
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              {t('gov.permits.detail.answers')}
            </Typography>
            {app.fields.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('gov.permits.detail.noQuestions')}
              </Typography>
            )}
            {app.fields.map((f) => (
              <Detail
                key={f.key}
                label={fr ? f.labelFr : f.labelEn}
                value={answerText(f, app.answers[f.key], fr, (v) =>
                  t(v ? 'common.yes' : 'common.no'),
                )}
              />
            ))}
          </HairlineCard>

          <Stack spacing={2.5} sx={{ flex: 1, width: '100%' }}>
            <HairlineCard sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {t('gov.permits.detail.documents')}
              </Typography>
              {app.documents.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('gov.permits.detail.noDocuments')}
                </Typography>
              )}
              <Stack spacing={1}>
                {app.documents.map((d) => {
                  const req = app.documentRequirements.find((r) => r.key === d.requirementKey);
                  return (
                    <Stack key={d.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Icons.Paperclip size={iconSize.sm} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Link
                          component="button"
                          onClick={() =>
                            downloadApplicationDocument(lg, app.id, d.id, d.filename).catch((err) =>
                              toast.error(err, 'gov.permits.errors.download'),
                            )
                          }
                          sx={{ textAlign: 'left', wordBreak: 'break-word' }}
                        >
                          {d.filename}
                        </Link>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {req
                            ? fr
                              ? req.labelFr
                              : req.labelEn
                            : t('gov.permits.detail.otherDocument')}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </HairlineCard>

            <HairlineCard sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {t('gov.permits.detail.history')}
              </Typography>
              <Stack spacing={1.25}>
                {app.history.map((e, i) => (
                  <Box key={i}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t(`gov.permits.events.${e.kind}`, { defaultValue: e.kind })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(e.at, i18n.language)} ·{' '}
                      {e.by === 'applicant'
                        ? t('gov.permits.detail.byApplicant')
                        : (e.actorName ?? t('gov.permits.detail.byStaff'))}
                    </Typography>
                    {e.message && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                        {e.message}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </HairlineCard>
          </Stack>
        </Stack>
      </Stack>

      <DecisionDialog
        prompt={prompt}
        app={app}
        text={text}
        setText={setText}
        waive={waive}
        setWaive={setWaive}
        pending={decide.isPending}
        onClose={() => setPrompt(null)}
        onConfirm={(decision, key) => void run(decision, key)}
      />
      {scheduling && (
        <ScheduleInspectionDialog
          lg={lg}
          open
          onClose={() => setScheduling(false)}
          initial={{ permitApplicationId: app.id, propertyAddress: app.propertyAddress }}
          onScheduled={(ins) => navigate(`/inspections/${ins.id}`)}
        />
      )}
    </AppShell>
  );
}

function Detail({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }} sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
        {label}
      </Typography>
      <Box sx={{ typography: 'body2', wordBreak: 'break-word' }}>{value}</Box>
    </Stack>
  );
}

function DecisionDialog({
  prompt,
  app,
  text,
  setText,
  waive,
  setWaive,
  pending,
  onClose,
  onConfirm,
}: {
  prompt: Prompt;
  app: PermitApplicationView;
  text: string;
  setText: (v: string) => void;
  waive: boolean;
  setWaive: (v: boolean) => void;
  pending: boolean;
  onClose: () => void;
  onConfirm: (decision: PermitDecision, successKey: string) => void;
}) {
  const { t } = useTranslation();
  if (!prompt) return null;
  const trimmed = text.trim();
  const minimum = prompt === 'approve' ? 0 : prompt === 'payment' ? 1 : 5;
  const blocked =
    pending ||
    trimmed.length < minimum ||
    (prompt === 'approve' && app.feeStatus === 'due' && !waive);

  const confirm = () => {
    switch (prompt) {
      case 'request-info':
        return onConfirm(
          { action: 'request-info', message: trimmed },
          'gov.permits.detail.infoRequested',
        );
      case 'payment':
        return onConfirm(
          { action: 'payment', reference: trimmed },
          'gov.permits.detail.paymentRecorded',
        );
      case 'approve':
        return onConfirm(
          { action: 'approve', conditions: trimmed || undefined, waiveFee: waive || undefined },
          'gov.permits.detail.approved',
        );
      case 'refuse':
        return onConfirm({ action: 'refuse', reason: trimmed }, 'gov.permits.detail.refused');
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t(`gov.permits.prompts.${prompt}.title`)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(`gov.permits.prompts.${prompt}.body`)}
          </Typography>
          <TextField
            label={t(`gov.permits.prompts.${prompt}.field`)}
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline={prompt !== 'payment'}
            minRows={prompt === 'payment' ? undefined : 3}
            fullWidth
            required={prompt !== 'approve'}
          />
          {prompt === 'approve' && app.feeStatus === 'due' && (
            <FormControlLabel
              control={<Checkbox checked={waive} onChange={(e) => setWaive(e.target.checked)} />}
              label={t('gov.permits.prompts.approve.waive')}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          color={prompt === 'refuse' ? 'error' : 'primary'}
          disabled={blocked}
          onClick={confirm}
        >
          {t(`gov.permits.prompts.${prompt}.confirm`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
